/**
 * Nav — the primary sidebar. Route list lives here and in router.tsx; keep them in step.
 *
 * Active state is carried three ways (accent left rule, weight, colour) rather than by
 * colour alone. Icons are inline SVG — no icon package, no emoji, and nothing that
 * needs a network fetch on a locked-down operator laptop.
 *
 * `counts` is optional badge data (e.g. failed runs today). Pass only numbers that are
 * worth interrupting someone for; a count on every item is a count on nothing.
 */

import { NavLink } from 'react-router-dom';
import type { ReactNode } from 'react';

export interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
  /** Match child routes too (/runs also lights up on /runs/abc). Default true. */
  nested?: boolean;
  description?: string;
}

export interface NavSection {
  label: string;
  items: NavItem[];
}

const icon = (paths: ReactNode): ReactNode => (
  <svg
    className="nav-icon"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {paths}
  </svg>
);

const ICONS = {
  clients: icon(
    <>
      <circle cx="6" cy="5.2" r="2.3" />
      <path d="M1.8 13.4c0-2.3 1.9-3.7 4.2-3.7s4.2 1.4 4.2 3.7" />
      <path d="M11 3.2a2.2 2.2 0 0 1 0 4.2M12.2 9.9c1.3.4 2.2 1.4 2.2 3.1" />
    </>,
  ),
  schedules: icon(
    <>
      <rect x="1.9" y="3" width="12.2" height="11" rx="1.6" />
      <path d="M1.9 6.4h12.2M5 1.8v2.4M11 1.8v2.4" />
      <path d="M8 8.6v2.3l1.6 1" />
    </>,
  ),
  runs: icon(
    <>
      <path d="M1.8 8h2.6l1.7-4.3 2.4 9L10.4 8h3.8" />
    </>,
  ),
  dashboard: icon(
    <>
      <rect x="1.9" y="2.4" width="5.2" height="5.2" rx="1.2" />
      <rect x="8.9" y="2.4" width="5.2" height="5.2" rx="1.2" />
      <rect x="1.9" y="8.9" width="5.2" height="5.2" rx="1.2" />
      <rect x="8.9" y="8.9" width="5.2" height="5.2" rx="1.2" />
    </>,
  ),
  downloads: icon(
    <>
      <path d="M8 1.9v7.6M5.1 6.8 8 9.7l2.9-2.9" />
      <path d="M2.2 10.6v2.1c0 .8.6 1.4 1.4 1.4h8.8c.8 0 1.4-.6 1.4-1.4v-2.1" />
    </>,
  ),
  sent: icon(
    <>
      <rect x="1.8" y="3.4" width="12.4" height="9.2" rx="1.5" />
      <path d="m2.4 4.4 5.6 4 5.6-4" />
    </>,
  ),
  dispositions: icon(
    <>
      <path d="M6.2 4.1h8M6.2 8h8M6.2 11.9h8" />
      <path d="M2.2 3.1h1.6v2.2M2.2 5.3h1.6" />
      <path d="M2.2 7.4h1.7L2.2 9.4h1.7M2.2 11.1h1.7v1.1H2.6v1.1h1.3" />
    </>,
  ),
} as const;

/**
 * The console's route map. Grouped by what an operator is doing:
 * "Dispatch" is the daily loop; "Configuration" is set-up that changes rarely.
 */
export const NAV_SECTIONS: NavSection[] = [
  {
    label: 'Dispatch',
    items: [
      {
        to: '/dashboard',
        label: 'Operations',
        icon: ICONS.dashboard,
        description: 'What is wrong now, and what is about to be',
      },
      { to: '/runs', label: 'Runs', icon: ICONS.runs, description: 'Every dispatch and its outcome' },
      { to: '/sent', label: 'Sent', icon: ICONS.sent, description: 'The mail archive — every copy as it was sent' },
      { to: '/downloads', label: 'Downloads', icon: ICONS.downloads, description: 'Generate and fetch a report' },
      { to: '/schedules', label: 'Schedules', icon: ICONS.schedules, description: 'What fires, when, for whom' },
    ],
  },
  {
    label: 'Configuration',
    items: [
      { to: '/clients', label: 'Clients', icon: ICONS.clients, description: 'Field maps, columns, recipients' },
      {
        to: '/dispositions',
        label: 'Dispositions',
        icon: ICONS.dispositions,
        description: 'Contact-outcome ranking',
      },
    ],
  },
];

export interface NavProps {
  /** Optional per-route counts, keyed by `to` (e.g. { '/runs': 3 }). */
  counts?: Record<string, number | undefined>;
  /** Called after a navigation — the mobile drawer uses it to close itself. */
  onNavigate?: () => void;
  sections?: NavSection[];
}

export function Nav({ counts, onNavigate, sections = NAV_SECTIONS }: NavProps) {
  return (
    <nav className="nav" aria-label="Primary">
      {sections.map((section) => (
        <div key={section.label}>
          <div className="nav-section-label">{section.label}</div>
          <div className="nav-items">
            {section.items.map((item) => {
              const count = counts?.[item.to];
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.nested === false}
                  className={({ isActive }) => `nav-item ${isActive ? 'is-active' : ''}`.trim()}
                  title={item.description}
                  onClick={onNavigate}
                >
                  {item.icon}
                  <span className="truncate">{item.label}</span>
                  {typeof count === 'number' && count > 0 ? (
                    <span className="nav-count">{count.toLocaleString('en-IN')}</span>
                  ) : null}
                </NavLink>
              );
            })}
          </div>
        </div>
      ))}
    </nav>
  );
}

export default Nav;
